import { getAllFiles, getFile, saveFile, type StoredFileRecord } from '../db/files-repo';
import type { AnthropicToolDefinition } from '../api/anthropic';

/** File-operation tools exposed to the agentic loop, keyed by name. A persona only gets the subset
 * named in its `allowedTools` — enforcement happens in executeTool, not just via prompting. */
export const AGENT_TOOLS: Record<string, AnthropicToolDefinition> = {
  read_file: {
    name: 'read_file',
    description: "Read the full contents of a file already loaded in the workspace's file explorer.",
    input_schema: {
      type: 'object',
      properties: { path: { type: 'string', description: 'Workspace-relative file path, e.g. src/main.ts' } },
      required: ['path'],
    },
  },
  list_files: {
    name: 'list_files',
    description: 'List every file path currently loaded in the workspace.',
    input_schema: { type: 'object', properties: {} },
  },
  search_files: {
    name: 'search_files',
    description:
      "Case-insensitive substring search across every loaded file's content. Returns matching lines with their file path and line number.",
    input_schema: {
      type: 'object',
      properties: { query: { type: 'string', description: 'Text to search for' } },
      required: ['query'],
    },
  },
  write_file: {
    name: 'write_file',
    description: 'Overwrite a file already loaded in the workspace with new content. The file must already exist in the workspace.',
    input_schema: {
      type: 'object',
      properties: {
        path: { type: 'string', description: 'Workspace-relative file path of an already-loaded file' },
        content: { type: 'string', description: 'The complete new file content' },
      },
      required: ['path', 'content'],
    },
  },
};

const MAX_SEARCH_RESULTS = 200;

export interface ToolExecutionContext {
  /** Tool names this persona may invoke at all. */
  allowedTools: ReadonlySet<string>;
  /** Extra guard specifically on write_file's target path (e.g. "must be a test file"). */
  canWritePath?: (path: string) => boolean;
  /** Lets the caller sync an open editor buffer when the file it's viewing gets written. */
  onFileWritten?: (path: string, content: string) => void;
}

export interface ToolCallResult {
  content: string;
  isError: boolean;
}

function asRecord(input: unknown): Record<string, unknown> {
  return typeof input === 'object' && input !== null ? (input as Record<string, unknown>) : {};
}

async function readFile(input: unknown): Promise<ToolCallResult> {
  const path = String(asRecord(input).path ?? '');
  const file = await getFile(path);
  if (!file) return { content: `No such file in the workspace: ${path}`, isError: true };
  return { content: file.content, isError: false };
}

async function listFiles(): Promise<ToolCallResult> {
  const files = await getAllFiles();
  const paths = files.map((f) => f.path).sort();
  return { content: paths.length ? paths.join('\n') : '(no files loaded in the workspace)', isError: false };
}

async function searchFiles(input: unknown): Promise<ToolCallResult> {
  const query = String(asRecord(input).query ?? '');
  if (!query) return { content: 'query must be a non-empty string', isError: true };
  const files = await getAllFiles();
  const needle = query.toLowerCase();
  const matches: string[] = [];
  for (const file of files) {
    const lines = file.content.split('\n');
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].toLowerCase().includes(needle)) {
        matches.push(`${file.path}:${i + 1}: ${lines[i].trim()}`);
        if (matches.length >= MAX_SEARCH_RESULTS) break;
      }
    }
    if (matches.length >= MAX_SEARCH_RESULTS) break;
  }
  return { content: matches.length ? matches.join('\n') : 'No matches.', isError: false };
}

async function writeFile(input: unknown, ctx: ToolExecutionContext): Promise<ToolCallResult> {
  const { path: rawPath, content: rawContent } = asRecord(input);
  const path = String(rawPath ?? '');
  const content = String(rawContent ?? '');
  if (ctx.canWritePath && !ctx.canWritePath(path)) {
    return { content: `This agent is not permitted to write to "${path}".`, isError: true };
  }
  const existing = await getFile(path);
  if (!existing) {
    return { content: `No such file in the workspace: ${path} (this agent can only edit files already loaded)`, isError: true };
  }
  const updated: StoredFileRecord = { ...existing, content, updatedAt: Date.now() };
  await saveFile(updated);
  ctx.onFileWritten?.(path, content);
  return { content: `Wrote ${content.length.toLocaleString()} characters to ${path}.`, isError: false };
}

/** Dispatches a single tool call by name, enforcing `ctx.allowedTools` before running anything. */
export async function executeTool(name: string, input: unknown, ctx: ToolExecutionContext): Promise<ToolCallResult> {
  if (!ctx.allowedTools.has(name)) {
    return { content: `Tool "${name}" is not available to this agent.`, isError: true };
  }
  try {
    switch (name) {
      case 'read_file':
        return await readFile(input);
      case 'list_files':
        return await listFiles();
      case 'search_files':
        return await searchFiles(input);
      case 'write_file':
        return await writeFile(input, ctx);
      default:
        return { content: `Unknown tool: ${name}`, isError: true };
    }
  } catch (err) {
    return { content: err instanceof Error ? err.message : String(err), isError: true };
  }
}
